import { useState, useEffect, useRef } from 'react';
import { useTheme } from '../utils/theme';
import { Play, Pause, SkipBack, SkipForward, Volume2, Maximize, ArrowLeft } from 'lucide-react';

const VideoCsv = ({ playsData = [], videoUrl = '', onBack = () => { } }) => {
    const { theme } = useTheme();
    const isDarkMode = true; // OLED: always dark
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(2400); // Will be updated when video loads
    const [currentPlay, setCurrentPlay] = useState(null);
    const [selectedPlay, setSelectedPlay] = useState(null);
    const [volume, setVolume] = useState(80);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const videoRef = useRef(null);

    // Process plays data to add timestamps and fix missing fields
    const processedPlaysData = playsData?.map((play, index) => {
        // Convert game clock to timestamp (in seconds from start of game)
        const convertClockToTimestamp = (clock, period) => {
            if (!clock || !period) return index * 60; // Fallback: distribute plays every 60 seconds

            // Parse clock like "9:30" or "18:02"
            const [minutes, seconds] = clock?.split(':')?.map(Number) || [];
            if (isNaN(minutes) || isNaN(seconds)) return index * 60;

            // Calculate seconds remaining in period (assuming 10-minute periods)
            const periodLength = 10 * 60; // 10 minutes in seconds
            const timeRemainingInPeriod = (minutes * 60) + seconds;
            const timeElapsedInPeriod = periodLength - timeRemainingInPeriod;

            // Add time from previous periods
            const previousPeriodsTime = (period - 1) * periodLength;

            return previousPeriodsTime + timeElapsedInPeriod;
        };

        return {
            ...play,
            timestamp: play?.timestamp || convertClockToTimestamp(play?.clock, play?.period),
            title: play?.title || play?.player || `Play ${play?.s_no || index + 1}`,
            team: play?.team || 'Unknown Team',
            play_type: play?.play_type || 'Unknown',
            result: play?.result || 'Unknown',
            duration: play?.duration || '00:30.0',
            period: play?.period || 1,
            clock: play?.clock || '0:00',
            synergy_string: play?.synergy_string || 'No details available',
            synergy_tags: play?.synergy_tags || '',
            description: play?.description || play?.synergy_string || 'No description available'
        };
    });

    // Get the first play from the array
    const firstPlay = processedPlaysData?.length > 0 ? processedPlaysData?.[0] : null;

    // Video event handlers
    useEffect(() => {
        const video = videoRef?.current;
        if (!video) return;

        const handleTimeUpdate = () => {
            setCurrentTime(Math.floor(video?.currentTime || 0));
        };

        const handleDurationChange = () => {
            setDuration(Math.floor(video?.duration || 0));
        };

        const handlePlay = () => {
            setIsPlaying(true);
        };

        const handlePause = () => {
            setIsPlaying(false);
        };

        video?.addEventListener('timeupdate', handleTimeUpdate);
        video?.addEventListener('durationchange', handleDurationChange);
        video?.addEventListener('play', handlePlay);
        video?.addEventListener('pause', handlePause);

        return () => {
            video?.removeEventListener('timeupdate', handleTimeUpdate);
            video?.removeEventListener('durationchange', handleDurationChange);
            video?.removeEventListener('play', handlePlay);
            video?.removeEventListener('pause', handlePause);
        };
    }, [videoUrl]);

    // Update video playback rate when speed changes
    useEffect(() => {
        if (videoRef?.current) {
            videoRef.current.playbackRate = playbackSpeed;
        }
    }, [playbackSpeed]);

    // Update video volume when volume changes
    useEffect(() => {
        if (videoRef?.current) {
            videoRef.current.volume = volume / 100;
        }
    }, [volume]);

    useEffect(() => {
        const handleKeyPress = (e) => {
            switch (e?.code) {
                case 'Space':
                    e?.preventDefault();
                    togglePlayPause();
                    break;
                case 'ArrowLeft':
                    e?.preventDefault();
                    skipBackward();
                    break;
                case 'ArrowRight':
                    e?.preventDefault();
                    skipForward();
                    break;
                case 'KeyF':
                    e?.preventDefault();
                    toggleFullscreen();
                    break;
                default:
                    break;
            }
        };

        window?.addEventListener('keydown', handleKeyPress);
        return () => window?.removeEventListener('keydown', handleKeyPress);
    }, []);

    const formatTime = (seconds) => {
        // Handle NaN or invalid values
        if (!seconds || isNaN(seconds) || seconds < 0) {
            return '0:00';
        }

        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
    };

    const getResultColor = (result) => {
        if (result?.includes('Make')) return 'text-green-600 bg-green-50 border-green-200';
        if (result?.includes('Miss')) return 'text-red-600 bg-red-50 border-red-200';
        if (result?.includes('Turnover')) return 'text-orange-600 bg-orange-50 border-orange-200';
        return 'text-gray-600 bg-gray-50 border-gray-200';
    };

    const getTeamAbbreviation = (team) => {
        if (!team || typeof team !== 'string') return 'UNK';
        const teamLower = team?.toLowerCase();
        if (teamLower?.includes('saint mary') || teamLower?.includes('gaels')) {
            return 'SMG';
        }
        if (teamLower?.includes('nevada') || teamLower?.includes('nev')) {
            return 'NEV';
        }
        if (teamLower?.includes('boise') || teamLower?.includes('bsu')) {
            return 'BSU';
        }
        if (teamLower?.includes('usc') || teamLower?.includes('trojans')) {
            return 'USC';
        }
        return 'UNK';
    };

    const getTeamColor = (team) => {
        if (!team || typeof team !== 'string') return 'text-gray-600 bg-gray-50';
        const teamLower = team?.toLowerCase();
        if (teamLower?.includes('saint mary') || teamLower?.includes('gaels')) {
            return 'text-red-600 bg-red-50';
        }
        if (teamLower?.includes('nevada') || teamLower?.includes('nev')) {
            return 'text-blue-600 bg-blue-50';
        }
        if (teamLower?.includes('boise') || teamLower?.includes('bsu')) {
            return 'text-purple-600 bg-purple-50';
        }
        if (teamLower?.includes('usc') || teamLower?.includes('trojans')) {
            return 'text-yellow-600 bg-yellow-50';
        }
        return 'text-gray-600 bg-gray-50';
    };

    const togglePlayPause = () => {
        if (videoRef?.current) {
            if (isPlaying) {
                videoRef?.current?.pause();
            } else {
                videoRef?.current?.play();
            }
        }
    };

    const jumpToPlay = (play) => {
        const timestamp = play?.timestamp || 0;
        if (videoRef?.current) {
            videoRef.current.currentTime = timestamp;
        }
        setSelectedPlay(play);
        setCurrentPlay(play); // Also set as current play for immediate display
        if (!isPlaying && videoRef?.current) {
            videoRef?.current?.play();
        }
    };

    const skipBackward = () => {
        if (videoRef?.current) {
            videoRef.current.currentTime = Math.max(0, videoRef?.current?.currentTime - 10);
        }
    };

    const skipForward = () => {
        if (videoRef?.current) {
            videoRef.current.currentTime = Math.min(duration, videoRef?.current?.currentTime + 10);
        }
    };

    const toggleFullscreen = () => {
        if (!isFullscreen) {
            if (videoRef?.current?.requestFullscreen) {
                videoRef?.current?.requestFullscreen();
            }
        } else {
            if (document?.exitFullscreen) {
                document?.exitFullscreen();
            }
        }
        setIsFullscreen(!isFullscreen);
    };

    const handleTimelineClick = (e) => {
        const rect = e?.currentTarget?.getBoundingClientRect();
        const clickPosition = (e?.clientX - rect?.left) / rect?.width;
        const newTime = clickPosition * duration;
        if (videoRef?.current) {
            videoRef.current.currentTime = newTime;
        }
    };

    return (
        <div className={`min-h-screen ${'bg-[#0a0a0a] text-gray-200'}`}>
            {/* Header */}
            <div className={`${'bg-gray-800 border-gray-700'} border-b px-6 py-4`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={onBack} className={`flex items-center gap-2 ${'text-gray-300 hover:text-white'} transition-colors`}>
                            <ArrowLeft className="w-5 h-5" />
                            <span className="font-medium">Back to Files</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto p-6 space-y-6">
                {/* Video Player Section */}
                <div className={`${'bg-gray-800'} rounded-xl shadow-lg overflow-hidden transition-all duration-300 ${isFullscreen ? 'fixed inset-0 z-50 rounded-none' : ''}`}>
                    <div className="relative bg-gray-900 aspect-video">
                        {/* Actual Video Element */}
                        <video
                            ref={videoRef}
                            src={videoUrl}
                            className="w-full h-full object-contain"
                            preload="metadata"
                            playsInline
                        />

                        {/* Fallback when no video URL */}
                        {!videoUrl && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="text-center">
                                    <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-4">
                                        <Play className="w-8 h-8 text-white ml-1" />
                                    </div>
                                    <div className="text-white text-lg font-semibold">No video available</div>
                                </div>
                            </div>
                        )}

                        {isFullscreen && (
                            <button
                                onClick={toggleFullscreen}
                                className="absolute top-4 right-4 z-10 p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-70 transition-colors"
                                title="Exit fullscreen (F)"
                            >
                                <Maximize className="w-5 h-5" />
                            </button>
                        )}

                        {currentPlay && (
                            <div className="absolute top-4 left-4 bg-black bg-opacity-70 rounded-lg p-3 text-white">
                                <div className="text-sm font-medium">{currentPlay?.title}</div>
                                <div className="text-xs text-gray-300">{currentPlay?.play_type} • {currentPlay?.clock}</div>
                            </div>
                        )}

                        {/* Video Controls Overlay */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                            <div className="flex items-center gap-4 text-white">
                                <button
                                    onClick={togglePlayPause}
                                    className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                                    title={isPlaying ? "Pause" : "Play"}
                                >
                                    {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                                </button>

                                <button
                                    onClick={skipBackward}
                                    className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                                    title="Skip back 10s"
                                >
                                    <SkipBack className="w-4 h-4" />
                                </button>

                                <button
                                    onClick={skipForward}
                                    className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                                    title="Skip forward 10s"
                                >
                                    <SkipForward className="w-4 h-4" />
                                </button>

                                <button
                                    onClick={() => setPlaybackSpeed(prev => prev === 1 ? 0.5 : prev === 0.5 ? 2 : 1)}
                                    className="px-2 py-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors text-xs font-medium"
                                    title="Playback speed"
                                >
                                    {playbackSpeed}x
                                </button>

                                <div className="flex-1 mx-4">
                                    <div className="flex items-center gap-2 text-sm">
                                        <span>{formatTime(currentTime)}</span>
                                        <div
                                            className="flex-1 bg-white bg-opacity-30 rounded-full h-1 relative cursor-pointer"
                                            onClick={handleTimelineClick}
                                            title="Click to seek"
                                        >
                                            <div
                                                className="bg-white rounded-full h-1 transition-all duration-300"
                                                style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                                            />
                                        </div>
                                        <span>{formatTime(duration)}</span>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                                        title={`Volume: ${volume}%`}
                                    >
                                        <Volume2 className="w-4 h-4" />
                                    </button>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={volume}
                                        onChange={(e) => setVolume(parseInt(e?.target?.value) || 0)}
                                        className="w-16 h-1 bg-white bg-opacity-30 rounded-full appearance-none cursor-pointer slider"
                                        title="Volume control"
                                        style={{
                                            background: `linear-gradient(to right, white 0%, white ${volume}%, rgba(255,255,255,0.3) ${volume}%, rgba(255,255,255,0.3) 100%)`
                                        }}
                                    />
                                </div>

                                <button
                                    onClick={toggleFullscreen}
                                    className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                                    title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                                >
                                    <Maximize className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* First Play Display */}
                {firstPlay && (
                    <div className={`${'bg-gray-800 border-gray-700'} rounded-xl shadow-sm border`}>
                        <div className={`p-6 border-b ${'border-gray-700'}`}>
                            <h3 className={`text-lg font-semibold ${'text-gray-100'}`}>Play Information</h3>
                        </div>

                        <div className="p-6">
                            <div
                                className={`p-6 rounded-lg border-2 ${'border-gray-700 bg-gradient-to-r from-gray-800 to-gray-700 hover:bg-gray-700'} cursor-pointer transition-all duration-200`}
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center font-bold text-blue-700">
                                            {firstPlay?.s_no}
                                        </div>
                                        <div>
                                            <h4 className={`font-bold text-xl ${'text-white'}`}>{firstPlay?.title}</h4>
                                            <p className={`${'text-gray-300'} font-medium`}>{firstPlay?.notes}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getTeamColor(firstPlay?.team)}`}>
                                            {firstPlay?.team}
                                        </span>
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getResultColor(firstPlay?.result)}`}>
                                            {firstPlay?.result}
                                        </span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                                    <div className={`${'bg-gray-700 border-gray-600'} p-3 rounded-lg border`}>
                                        <div className={`text-xs ${'text-gray-400'} uppercase tracking-wide`}>Game</div>
                                        <div className={`font-semibold ${'text-gray-100'}`}>{firstPlay?.game}</div>
                                        <div className={`text-sm ${'text-gray-300'}`}>{firstPlay?.game_date}</div>
                                    </div>
                                    <div className={`${'bg-gray-700 border-gray-600'} p-3 rounded-lg border`}>
                                        <div className={`text-xs ${'text-gray-400'} uppercase tracking-wide`}>Period & Clock</div>
                                        <div className={`font-semibold ${'text-gray-100'}`}>Period {firstPlay?.period}</div>
                                        <div className={`text-sm ${'text-gray-300'}`}>{firstPlay?.clock}</div>
                                    </div>
                                    <div className={`${'bg-gray-700 border-gray-600'} p-3 rounded-lg border`}>
                                        <div className={`text-xs ${'text-gray-400'} uppercase tracking-wide`}>Player</div>
                                        <div className={`font-semibold ${'text-gray-100'}`}>{firstPlay?.player}</div>
                                        <div className={`text-sm ${'text-gray-300'}`}>{firstPlay?.team}</div>
                                    </div>
                                    <div className={`${'bg-gray-700 border-gray-600'} p-3 rounded-lg border`}>
                                        <div className={`text-xs ${'text-gray-400'} uppercase tracking-wide`}>Duration</div>
                                        <div className={`font-semibold ${'text-gray-100'}`}>{firstPlay?.duration}</div>
                                        <div className={`text-sm ${'text-gray-300'}`}>Play Length</div>
                                    </div>
                                </div>

                                {firstPlay?.synergy_string && (
                                    <div className={`${'bg-gray-700 border-gray-600'} p-4 rounded-lg border`}>
                                        <h5 className={`font-medium ${'text-gray-100'} mb-2`}>Synergy Breakdown</h5>
                                        <p className={`text-sm ${'text-gray-300'} font-mono leading-relaxed`}>
                                            {firstPlay?.synergy_string}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {!firstPlay && processedPlaysData?.length === 0 && (
                    <div className={`${'bg-gray-800 border-gray-700'} rounded-xl shadow-sm border p-6`}>
                        <div className="text-center py-8">
                            <div className={`${'text-gray-400'}`}>No play data available</div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VideoCsv;